import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '@src/model';
import { useRequest } from 'ahooks';
import { getResumeEnum, getResumeDetail, getCommonResume, getUserInfo } from '@service/resumeDetail';
import { Loading } from '@ss/mtd-react';
import { SOURCE_TYPE, LX_RESUME_EDIT, RESUME_EDITOR_CALLER } from '@common/const';
import { LxPageView } from '@utils/siteLx';
import UploadResume from './uploadResume';
import BaseInfo from './baseInfo';
import EduExperience from './eduExperience';
import WorkExperience from './workExperience';
import ProjectExperience from './projectExperience';
import Honor from './honor';
import LanguageStandard from './languageStandard';
import Certificate from './certificate';
import SkillStandard from './skillStandard';
import Paper from './paper';
import Competition from './competition';
import Patent from './patent';
import Exam from './exam';
import SocialAccount from './socialAccount';
import SelfDescription from './selfDescription';
import SampleReel from './sampleReel';

import './index.scss';
import CampusExperience from './campusExperience';

type ResumeEditorCaller = (typeof RESUME_EDITOR_CALLER)[keyof typeof RESUME_EDITOR_CALLER];
interface IProps {
  type: string;
  setChecked?: Function;
  resumeEnum?: any;
  resetHaveIntentionApplyErrorList?: () => void;
  caller: ResumeEditorCaller; // 使用 ResumeDetailEdit 的调用方
  ifCampusProjectIntern?: boolean | null; // 是否是项目实习生
}

const fromCampusProjectIntern = 'fromCampusProjectIntern';
const ResumeDetailEdit: FC<IProps> = (props) => {
  const {
    type,
    setChecked,
    resumeEnum,
    resetHaveIntentionApplyErrorList,
    caller,
    ifCampusProjectIntern = null,
  } = props;

  const { campusResumeDetail, socialResumeDetail, socialResumeFormRefStore, campusResumeFormRefStore } = useRootStore();
  const [loading, setLoading] = useState(true);
  const [storeLoading, setStoreLoading] = useState(false);

  let store;
  let storeRef;
  if (type === 'campus') {
    storeRef = campusResumeFormRefStore;
    store = campusResumeDetail;
  }

  if (type === 'social') {
    storeRef = socialResumeFormRefStore;
    store = socialResumeDetail;
  }

  // 获取用户手机号
  const { runAsync: runGetPhone } = useRequest(getUserInfo, {
    manual: true,
    onSuccess: async (result) => {
      setStoreLoading(true);

      store.setResumeBaseInfo('phone', result?.phone ? result.phone : '');
      store.setResumeBaseInfo('interCode', result?.countryCode ? result.countryCode : '');
      store.setResumeBaseInfo('interCodeName', result.areaName ? result.areaName : '');
      setStoreLoading(false);
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  // 获取简历公共字段
  const { run: runCommonResume } = useRequest((source) => getCommonResume(source), {
    manual: true,
    onSuccess: (result) => {
      store.setStoreData(result);
      // 如果不存在简历，需要调用信息接口获取手机号
      if (!result?.resumeVo?.resumeBaseInfo?.phone) {
        runGetPhone();
      }
      // 如果不存在教育经历，需要添加一段空白的教育经历
      if (!result?.resumeVo?.resumeEduExpList?.length) {
        store.addResumeList('resumeEduExpList');
      }
      // 如果不存在实习，需要添加一段空白的实习经历
      if (!result?.resumeVo?.resumeLanguageList?.length && type === 'campus') {
        store.addResumeList('resumeLanguageList');
      }
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  // 获取简历详情 (简历详情页数据)
  const { run: runData } = useRequest((source) => getResumeDetail({ source: source?.source }), {
    manual: true,
    onSuccess: (result, _args) => {
      // 校招 - 项目实习生，查不到信息需要手动查一次common
      if (!result && _args?.[0]?.ext === fromCampusProjectIntern) {
        runCommonResume({ source: SOURCE_TYPE.campus });
        return;
      }
      store.setStoreData(result);
      // 如果不存在教育经历，需要添加一段空白的教育经历
      if (!result?.resumeVo?.resumeEduExpList?.length) {
        store.addResumeList('resumeEduExpList');
      }
      if (!result?.resumeVo?.resumeLanguageList?.length && type === 'campus') {
        store.addResumeList('resumeLanguageList');
      }
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  const getResumeDefaultData = (resumeExistStatus) => {
    // 校招 - 项目实习生 (且编辑来源为：岗位详情 - 申请 )
    if (type === 'campus' && caller === RESUME_EDITOR_CALLER.deliveryConfirm && ifCampusProjectIntern === true) {
      // source 都需要传 campus 的，且runData查不到数据时需要手动调用runCommonResume
      if (resumeExistStatus?.campusResume) runData({ source: SOURCE_TYPE.campus, ext: fromCampusProjectIntern });
      else runCommonResume({ source: SOURCE_TYPE.campus });
      return;
    }

    if (type === 'social') {
      if (resumeExistStatus?.socialResume) runData({ source: SOURCE_TYPE.social });
      else runCommonResume({ source: SOURCE_TYPE.social });
    }
    if (type === 'campus') {
      if (resumeExistStatus?.campusResume) runData({ source: SOURCE_TYPE.campus });
      else runCommonResume({ source: SOURCE_TYPE.campus });
    }
  };

  const { run: runResumeEnum } = useRequest(getResumeEnum, {
    manual: true,
    onSuccess: (res) => {
      getResumeDefaultData(res);
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    LxPageView({ cid: LX_RESUME_EDIT.resumeEditView, valLab: { resumeType: type } });
  }, []);

  // 初始数据获取
  useEffect(() => {
    // 如果没有简历类型枚举，需要重新请求一次
    if (!resumeEnum) runResumeEnum();
    else {
      getResumeDefaultData(resumeEnum);
    }
  }, [resumeEnum, type]);

  useEffect(() => {
    if (storeRef?.currentScrollRef) {
      setChecked?.(false);
      const node = document.getElementsByClassName(storeRef.currentScrollRef)[0];
      // @ts-ignore
      if (node?.offsetTop) {
        // @ts-ignore
        window.scrollTo({ top: node.offsetTop - 100, behavior: 'smooth' });
        storeRef.currentScrollRef = null;
      }
    }
  }, [loading, storeLoading]);

  // 加载中
  if (loading || storeLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Loading size="large" message="加载中" direction="vertical" />
      </div>
    );
  }

  const {
    resumeBaseInfo,
    resumeEduExpList,
    resumeWorkExpList,
    resumeProjectExpList,
    resumeCampusExpList,
    resumeHonorList,
    resumeLanguageList,
    resumeCertificateList,
    resumeSkillList,
    resumeThesisList,
    resumeCompetitionList,
    resumePatentList,
    resumeExamList,
    resumeSocialAccountList,
    resumeWorksAttachmentList, // 作品集
    resumeAttachmentList, // 简历附件
    description,
  } = store || {};

  return (
    <div className="resume_detail_edit">
      <UploadResume type={type} data={resumeAttachmentList} />
      <BaseInfo type={type} data={resumeBaseInfo} />
      <EduExperience
        type={type}
        data={resumeEduExpList}
        resetHaveIntentionApplyErrorList={resetHaveIntentionApplyErrorList}
      />
      {type === 'campus' && <LanguageStandard type={type} data={resumeLanguageList} />}
      <WorkExperience type={type} data={resumeWorkExpList} />
      <ProjectExperience type={type} data={resumeProjectExpList} />
      {type === 'campus' && <CampusExperience type={type} data={resumeCampusExpList} />}
      <Honor type={type} data={resumeHonorList} />
      {type === 'social' && <LanguageStandard type={type} data={resumeLanguageList} />}
      <Certificate type={type} data={resumeCertificateList} />
      {type === 'campus' && <SkillStandard data={resumeSkillList} />}
      {type === 'campus' && <Paper data={resumeThesisList} />}
      {type === 'campus' && <Competition data={resumeCompetitionList} />}
      {type === 'social' && <Patent data={resumePatentList} />}
      {type === 'social' && <Exam data={resumeExamList} />}
      {type === 'social' && <SocialAccount data={resumeSocialAccountList} />}
      <SampleReel type={type} data={resumeWorksAttachmentList} />
      {type === 'social' && <SelfDescription data={description} />}
    </div>
  );
};

export default observer(ResumeDetailEdit);
